import { Link } from "react-router-dom";
import { ShieldCheck, Cookie, Mail, MapPin, Phone } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import Seo from "@/components/Seo";
import { BRAND } from "@/lib/data";
import { reopenConsent } from "@/components/ConsentBanner";

export default function Privacy() {
  const updated = "July 2026";
  return (
    <main data-testid="privacy-page" className="pt-32 pb-28 relative z-10 bg-[#F9F8F6]">
      <Seo
        title="Privacy Policy | ZEBO India Private Limited"
        description="How ZEBO India Private Limited collects, uses and protects your data — cookies, consultation leads, AI Studio images and third-party tools."
        path="/privacy"
      />

      <div className="max-w-3xl mx-auto px-5 sm:px-8">
        <Reveal>
          <p className="overline text-[#8B0000] mb-4 flex items-center gap-2">
            <ShieldCheck size={14} /> Privacy & Cookies
          </p>
          <h1 className="font-serif text-5xl sm:text-6xl font-light tracking-tight text-[#143D63] leading-[0.95]">
            Your privacy matters.
          </h1>
          <p className="text-[#143D63]/70 mt-6 leading-relaxed">
            This policy explains what we collect when you use{" "}
            <span className="font-semibold">thezebo.com</span> and how we use it. Last updated: {updated}.
          </p>
        </Reveal>

        <div className="mt-14 space-y-12 text-[#143D63]/80 leading-relaxed">
          <Section title="1. Who we are">
            <p>
              {BRAND.full} (“ZEBO”, “we”, “us”) operates the website{" "}
              <span className="font-semibold">thezebo.com</span>. Our studio is at{" "}
              {BRAND.address}. You can reach us at{" "}
              <a href={`mailto:${BRAND.email}`} className="text-[#8B0000] link-underline">
                {BRAND.email}
              </a>{" "}
              or{" "}
              <a href={`tel:${BRAND.phoneRaw}`} className="text-[#8B0000] link-underline">
                {BRAND.phone}
              </a>
              .
            </p>
          </Section>

          <Section title="2. What we collect">
            <ul className="list-disc pl-5 space-y-2">
              <li>
                <b>Consultation details</b> — name, phone, email, city, property type, project scope, budget and any message you submit through our consultation form.
              </li>
              <li>
                <b>AI Studio inputs</b> — the room type, style, colour theme and notes you enter, and the resulting AI-generated image / 360° panorama.
              </li>
              <li>
                <b>Website analytics</b> — anonymised usage data (pages viewed, device, approximate location) via Google Analytics 4, subject to your cookie consent.
              </li>
              <li>
                <b>Advertising signals</b> — Google Ads conversion events (e.g. when a lead is submitted), subject to your cookie consent.
              </li>
            </ul>
          </Section>

          <Section title="3. How we use your data">
            <ul className="list-disc pl-5 space-y-2">
              <li>To respond to your consultation request and design your space.</li>
              <li>To send you an occasional update about your project.</li>
              <li>To measure and improve the website and our advertising.</li>
              <li>To fulfil our legal and accounting obligations.</li>
            </ul>
            <p className="mt-4">
              We do <b>not</b> sell your data. We do not share your consultation details with third parties for marketing.
            </p>
          </Section>

          <Section title="4. Cookies & Google Consent Mode v2">
            <p>
              We use cookies for two purposes: <b>essential</b> (site functioning, security) and{" "}
              <b>non-essential</b> (Google Analytics 4 and Google Ads). We follow{" "}
              <a
                href="https://developers.google.com/tag-platform/security/guides/consent"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#8B0000] link-underline"
              >
                Google Consent Mode v2
              </a>
              : until you accept, analytics and advertising cookies are set to “denied”.
            </p>
            <p className="mt-4">
              You can change your choice anytime:
            </p>
            <button
              data-testid="privacy-cookie-prefs"
              onClick={reopenConsent}
              className="mt-3 inline-flex items-center gap-2 border border-[#143D63] text-[#143D63] px-5 py-2.5 text-sm font-bold hover:bg-[#143D63] hover:text-[#F9F8F6] transition-colors"
            >
              <Cookie size={14} /> Manage cookie preferences
            </button>
          </Section>

          <Section title="5. Third-party services we use">
            <ul className="list-disc pl-5 space-y-2">
              <li>
                <b>Google Analytics 4</b> — measurement of site traffic and engagement (anonymised).
              </li>
              <li>
                <b>Google Ads</b> — conversion measurement and remarketing.
              </li>
              <li>
                <b>Google Maps</b> — embedded map on our website.
              </li>
              <li>
                <b>Resend</b> — sends lead notification emails to our team.
              </li>
              <li>
                <b>Google AI (Gemini)</b> — powers our AI Studio room-design generator. Your text prompts are processed to create images and are not used to train models on personal data.
              </li>
            </ul>
          </Section>

          <Section title="6. Google reviews">
            <p>
              If you choose to leave us a Google review, that review, your Google display name and photo become public on Google Business Profile. That is governed by{" "}
              <a
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#8B0000] link-underline"
              >
                Google&apos;s privacy policy
              </a>
              , not ours.
            </p>
          </Section>

          <Section title="7. Data retention">
            <p>
              We keep consultation records for as long as needed to service your project and to meet accounting requirements under Indian law. AI Studio generations linked to a lead are stored with that lead record. Cookie preferences are stored in your browser only.
            </p>
          </Section>

          <Section title="8. Your rights">
            <p>
              You may ask us to (a) confirm what data we hold about you, (b) correct it, (c) delete it, or (d) stop using it for marketing. Email{" "}
              <a href={`mailto:${BRAND.email}`} className="text-[#8B0000] link-underline">
                {BRAND.email}
              </a>{" "}
              and we will respond within 30 days.
            </p>
          </Section>

          <Section title="9. Security">
            <p>
              We use industry-standard encryption in transit (HTTPS) and access controls for our admin dashboard. No system is perfectly secure — please avoid sharing sensitive documents (identity documents, bank details) through the consultation form.
            </p>
          </Section>

          <Section title="10. Changes">
            <p>
              We may update this policy as our services evolve. The “Last updated” date at the top will reflect any change. Continued use of the website after an update means you accept the revised policy.
            </p>
          </Section>

          <Section title="11. Contact us">
            <ul className="space-y-2">
              <li className="flex items-start gap-2">
                <Mail size={16} className="text-[#8B0000] mt-1" />
                <a href={`mailto:${BRAND.email}`} className="link-underline">
                  {BRAND.email}
                </a>
              </li>
              <li className="flex items-start gap-2">
                <Phone size={16} className="text-[#8B0000] mt-1" />
                <a href={`tel:${BRAND.phoneRaw}`} className="link-underline">
                  {BRAND.phone}
                </a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin size={16} className="text-[#8B0000] mt-1" />
                <span>{BRAND.address}</span>
              </li>
            </ul>
          </Section>

          <div className="pt-8 border-t border-[#143D63]/10 flex items-center justify-between flex-wrap gap-3">
            <Link
              to="/"
              data-testid="privacy-back-home"
              className="text-[#8B0000] font-semibold link-underline"
            >
              &larr; Back to home
            </Link>
            <Link
              to="/terms"
              data-testid="privacy-terms-link"
              className="text-[#143D63] font-semibold link-underline"
            >
              Terms of Service &rarr;
            </Link>
          </div>
        </div>
      </div>
    </main>
  );
}

function Section({ title, children }) {
  return (
    <section data-testid={`privacy-section-${title.split(".")[0].trim()}`}>
      <h2 className="font-serif text-2xl sm:text-3xl text-[#143D63] font-light mb-4">
        {title}
      </h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}
